import React, { useEffect, useState, useRef } from "react";
import IntlMessages from "util/IntlMessages";
import { Card, Table, Row, Col, Button, Tooltip, Menu, Dropdown, Input, Space, message, Select } from "antd";
import { useSelector } from "react-redux";
import {
  DeleteTwoTone,
  EditTwoTone,
  MedicineBoxOutlined,
  EllipsisOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { showConfirm, getRole } from "../../../helpers";
import { useNavigate } from "react-router-dom";
import {
  useGetProductListQuery,
  useDeleteProductMutation,
  useAddProductMutation,
  useEditProductMutation,
  useGetSingleProductQuery,
} from "../../../appRedux/api/adminProductsApi";
import SearchOutlinedIcon from "assets/images/icons/search_outlined.svg";
import SearchFilledIcon from "assets/images/icons/search_filled.svg";
import "./product-list.css";
import deleteTempIcon from "assets/images/icons/delete.svg";
import editIcon from "assets/images/icons/Edit.svg";
import ProductDrawer from "../../../components/ProductDrawer";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import CustomPagination from "../../../components/CustomPagination";
import { useConfirmationPopup } from "../../../hooks/useConfirmationPopup";
import LoadingBar from "../../../components/LoadingBar";

const CustomFilterDropdown = ({
  options,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder = "Search..."
}) => (
  <div style={{ padding: 8, width: 240 }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
      <Select
        mode="multiple"
        placeholder={placeholder}
        value={selectedKeys}
        onChange={setSelectedKeys}
        showSearch
        style={{
          width: '100%',
          minHeight: 20,
          marginBottom: 0,
          display: 'block'
        }}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        maxTagCount="responsive"
        allowClear={false}
        dropdownRender={menu => (
          <>
            {menu}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              padding: '0 8px'
            }}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm && confirm();
                }}
                style={{ flex: 1 }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => confirm && confirm()}
                style={{ flex: 1 }}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      >
        {options.map(option => (
          <Select.Option key={option} value={option}>
            {option}
          </Select.Option>
        ))}
      </Select>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <Button
        size="small"
        style={{ flex: 1 }}
        onClick={() => {
          clearFilters && clearFilters();
          confirm && confirm();
        }}
      >
        Reset
      </Button>
      <Button
        type="primary"
        size="small"
        style={{ flex: 1 }}
        onClick={() => confirm && confirm()}
      >
        Apply
      </Button>
    </div>
  </div>
);

const ProductList = (props) => {
  const navigate = useNavigate();
  const searchInput = useRef(null);
  const confirmationPopup = useConfirmationPopup();

  // Separate filter states for each column
  const [filterStates, setFilterStates] = useState({
    name: {
      searchText: '',
      searchedColumn: ''
    },
    therapyArea: {
      searchText: '',
      searchedColumn: ''
    }
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [filterParams, setFilterParams] = useState({
    product_name: undefined,
    category: undefined,
    therapy_area: undefined
  });

  const [sortOrder, setSortOrder] = useState('product_name'); // Default ascending sort by product name

  // RTK Query hooks with pagination params
  const { data: apiResponse, isLoading, isFetching } = useGetProductListQuery({
    limit: Number(pagination.pageSize) || 10,
    offset: Number((pagination.current || 1) - 1) * Number(pagination.pageSize) || 0,
    ordering: sortOrder,
    ...filterParams
  });
  const [deleteProduct] = useDeleteProductMutation();
  const [addProduct] = useAddProductMutation();
  const [editProduct] = useEditProductMutation();

  const [data, setData] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Get single product for editing
  const { data: singleProductData, isLoading: isLoadingSingleProduct } = useGetSingleProductQuery(
    editingProduct?.key,
    {
      skip: !editingProduct, // Only fetch when editingProduct is set
    }
  );

  useEffect(() => {
    setIsUserAdmin(() => {
      const role = getRole();
      return role === "ADMIN" || role === "DIAGNOSTIC_ADMIN";
    });
  }, []);

  useEffect(() => {
    if (apiResponse) {
      setTotalCount(apiResponse.count || 0);

      // Process the results array
      let rowData = apiResponse.results && apiResponse.results.length
        ? apiResponse.results.map((value) => ({
          key: value.product_id,
          name: value.product_name,
          category: value.category,
          therapyArea: value.therapy_area,
        }))
        : [];
      setData(rowData);
    }
  }, [apiResponse]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setFilterStates(prev => ({
      ...prev,
      [dataIndex]: {
        searchText: selectedKeys[0],
        searchedColumn: dataIndex
      }
    }));
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when filtering

    // Update filter params based on the search field
    if (dataIndex === 'name') {
      setFilterParams(prev => ({ ...prev, product_name: selectedKeys[0] || undefined }));
    } else if (dataIndex === 'therapyArea') {
      setFilterParams(prev => ({ ...prev, therapy_area: selectedKeys[0] || undefined }));
    }
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    setFilterStates(prev => ({
      ...prev,
      [dataIndex]: {
        searchText: '',
        searchedColumn: ''
      }
    }));

    // Clear the specific filter param
    if (dataIndex === 'name') {
      setFilterParams(prev => ({ ...prev, product_name: undefined }));
    } else if (dataIndex === 'therapyArea') {
      setFilterParams(prev => ({ ...prev, therapy_area: undefined }));
    }
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0] || ''}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      filtered
        ? <img src={SearchFilledIcon} alt="Search Filled" style={{ width: 16, height: 16 }} />
        : <img src={SearchOutlinedIcon} alt="Search Outlined" style={{ width: 16, height: 16 }} />
    ),
    onFilter: (value, record) => {
      if (!value || value.trim() === '') return true;
      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    filteredValue: filterStates[dataIndex]?.searchedColumn === dataIndex ? [filterStates[dataIndex]?.searchText] : [],
  });

  const showDrawer = () => {
    setEditMode(false);
    setEditingProduct(null);
    setIsDrawerVisible(true);
  };

  const showEditDrawer = (item) => {
    setEditMode(true);
    setEditingProduct(item);
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setEditMode(false);
    setEditingProduct(null);
  };

  const handleAddProduct = async (values) => {
    setIsSubmitting(true);
    try {
      await addProduct(values).unwrap();
      closeDrawer();
    } catch (error) {
      console.error('Failed to add product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProduct = async (values) => {
    setIsSubmitting(true);
    try {
      await editProduct({
        id: editingProduct.key,
        data: values
      }).unwrap();
      closeDrawer();
    } catch (error) {
      console.error('Failed to edit product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (values) => {
    if (editMode) {
      handleEditProduct(values);
    } else {
      handleAddProduct(values);
    }
  };

  const navigateToEditProduct = (item) => {
    showEditDrawer(item);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });

    // Handle sorting
    if (sorter && sorter.field) {
      const sortFieldMap = {
        name: 'product_name', // Map frontend 'name' field to backend 'product_name'
      };

      const backendField = sortFieldMap[sorter.field] || sorter.field;
      const ordering = sorter.order === 'descend' ? `-${backendField}` : backendField;
      setSortOrder(ordering);
    } else {
      setSortOrder('product_name'); // Reset to default ascending sort by product name
    }

    // Update filter params based on the category filter only
    setFilterParams(prev => ({
      ...prev,
      category: filters.category?.[0]
    }));
  };

  const columns = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      ellipsis: true,
      sorter: true,
      ...getColumnSearchProps('name', "Product Name"),
      render: (name) => <span>{name}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      align: "center",
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <CustomFilterDropdown
          options={["FDF", "API", "Diagnostics"]}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          placeholder="Select categories..."
        />
      ),
      onFilter: (value, record) => record.category.includes(value),
      filteredValue: filteredInfo.category,
    },
    {
      title: "Therapy Area",
      dataIndex: "therapyArea",
      key: "therapyArea",
      align: "center",
      ...getColumnSearchProps('therapyArea', "Therapy Area"),
    },
    {
      title: "Action",
      key: "operation",
      align: "center",
      render: (item) => {
        const menu = (
          <Menu style={{ padding: "12px !important" }} className="actionListdropdown" id="product-list-action-dropdown">
            <Menu.Item className="actionDropdownTitle">Actions</Menu.Item>

            <Menu.Item
              key="edit"
              onClick={() => navigateToEditProduct(item)}
              id={"edit-product-" + item.key}
              disabled={!isUserAdmin}
              icon={<img src={editIcon} alt="Edit" width={16} height={16} />}
            >
              Edit
            </Menu.Item>

            <Menu.Item
              key="delete"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                showDeleteProduct(item);
              }}
              id={"delete-product-" + item.key}
              disabled={!isUserAdmin}
              icon={
                <img src={deleteTempIcon} alt="Deactivate" width={16} height={16} />
              }
            >
              Deactivate
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button
              type="link"
              icon={<EllipsisOutlined style={{ fontSize: "20px" }} />}
            />
          </Dropdown>
        );
      },
    },
  ];

  const showDeleteProduct = (item) => {
    if (confirmationPopup.isVisible) return;
    confirmationPopup.showDeleteConfirmation(
      `the product "${item.name.toUpperCase()}"`,
      async () => {
        try {
          await deleteProduct(item.key).unwrap();
          // Reset search and filters after successful deletion
          message.success("Product deleted successfully");
          setFilterStates({
            name: { searchText: '', searchedColumn: '' },
            therapyArea: { searchText: '', searchedColumn: '' }
          });
          setFilterParams({
            product_name: undefined,
            category: undefined,
            therapy_area: undefined
          });
          // Reset pagination to first page
          setPagination({
            current: 1,
            pageSize: pagination.pageSize,
          });
        } catch (error) {
          message.error("Failed to delete product");
          console.error("Failed to delete product:", error);
        }
      },
      () => {
        console.log("Delete cancelled");
      }
    );
  };

  // Show loading either during initial load or when fetching during pagination
  const isTableLoading = isLoading || isFetching;

  return (
    <div className="mpp-product-list">
      <Row>
        <Col span={24}>
          <div className="gx-flex-row align-items-center">
            <h1 className="title gx-mb-4">
              <IntlMessages id="sidebar.productList" />
            </h1>
            <Button
              id="add-product-btn"
              type="primary"
              className="gx-ml-auto"
              onClick={showDrawer}
              disabled={!isUserAdmin}
            >
              <MedicineBoxOutlined />{" "}
              <IntlMessages id="product.list.addProduct" />
            </Button>
          </div>
        </Col>

        {isTableLoading ? (
          <LoadingBar loading={isTableLoading} />
        ) : (
          <div className="gx-w-100">
            <Table
              className="gx-table-responsive mpp-list-table"
              columns={columns}
              dataSource={data}
              bordered
              pagination={false}
              onChange={handleTableChange}
              scroll={{
                y: 'calc(100vh - 250px)',
              }}
              sticky={{
                offsetHeader: 0
              }}
            />

            {/* Custom Pagination */}
            <CustomPagination
              current={pagination.current}
              total={totalCount}
              pageSize={pagination.pageSize}
              onChange={(page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page || 1,
                  pageSize: pageSize || prev.pageSize,
                }));
              }}
              showSizeChanger={true}
              pageSizeOptions={['10', '20', '50', '100']}
            />
          </div>
        )}
      </Row>

      {isSubmitting || isLoadingSingleProduct ? (
        <LoadingBar loading={isSubmitting || isLoadingSingleProduct} />
      ) : (
        <ProductDrawer
          visible={isDrawerVisible}
          onClose={closeDrawer}
          onSubmit={handleSubmit}
          editMode={editMode}
          initialValues={editMode && singleProductData ? {
            product_name: singleProductData.product_name,
            category: singleProductData.category,
            therapy_area: singleProductData.therapy_area
          } : null}
        />
      )}

      {/* Confirmation Popup */}
      <ConfirmationPopup
        visible={confirmationPopup.isVisible}
        title={confirmationPopup.config.title}
        message={confirmationPopup.config.message}
        confirmText={confirmationPopup.config.confirmText}
        cancelText={confirmationPopup.config.cancelText}
        confirmButtonType={confirmationPopup.config.confirmButtonType}
        onConfirm={confirmationPopup.handleConfirm}
        onCancel={confirmationPopup.handleCancel}
      />
    </div>
  );
};

export default ProductList;